import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiFetch, establishCsrf } from '../lib/apiClient';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const checkAuth = async () => {
      try {
        console.log('[AuthContext] Checking session validity (/api/auth/me)...');
        const res = await apiFetch('/api/auth/me', { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          console.log('[AuthContext] Active session verified for:', data.email, 'roles:', data.roles);
          if (active) setUser(data);
        } else if (res.status === 401) {
          console.info('[AuthContext] Session expired or not present (401)');
          if (active) setUser(null);
        }
      } catch (err) {
        console.warn('[AuthContext] Network issue during session check, retaining local state:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    checkAuth();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AuthContext] App became visible/focused, refreshing session...');
        checkAuth();
      }
    };

    // Rolling ping every 10 minutes when tab is open to keep session and cookies continuously renewed
    const pingInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('[AuthContext] Periodic 10-minute keepalive ping...');
        checkAuth();
      }
    }, 10 * 60 * 1000);

    const unauthorized = () => {
      console.warn('[AuthContext] Received gema:unauthorized event, resetting user state');
      setUser(null);
    };
    window.addEventListener('gema:unauthorized', unauthorized);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      active = false;
      clearInterval(pingInterval);
      window.removeEventListener('gema:unauthorized', unauthorized);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  const login = async (email, password) => {
    console.log('[AuthContext] Establishing CSRF token before login...');
    await establishCsrf();
    console.log('[AuthContext] CSRF established. Submitting POST /api/auth/login...');
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData,
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({ detail: 'Credenciales inválidas' }));
      console.warn('[AuthContext] Login failed:', res.status, errData);
      throw new Error(errData.detail || 'Credenciales inválidas');
    }
    const data = await res.json();
    console.log('[AuthContext] Login successful for user:', data.email, 'roles:', data.roles);
    setUser(data);
    return data;
  };

  const logout = async () => {
    console.log('[AuthContext] Logging out user...');
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      console.log('[AuthContext] Logout complete');
    }
  };

  return (
    <AuthContext.Provider value={{ token: null, user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
